import {
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  inject,
} from '@angular/core';

/**
 * The nearest ancestor that is scrolling vertically right now (the viewport when there is none). An ancestor with
 * `overflow: auto` but no bounded height grows with its content and never scrolls, so it is skipped: using it as the
 * observer root would count the whole list as visible.
 */
export function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const overflowY = getComputedStyle(parent).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight + 1)
      return parent;
  }
  return null;
}

/**
 * Put on an empty element at the end of a list: `loadMore` fires when it scrolls into (or near) view, so the page can
 * fetch the next batch. Whenever `appLoadMore` changes (pass the number of items loaded) the element is observed
 * again, so a list that is still too short after a batch keeps loading until the screen is full.
 */
@Directive({ selector: '[appLoadMore]', standalone: true })
export class LoadMoreDirective implements OnInit, OnChanges {
  /** Any value that changes after each batch, normally the loaded item count. */
  @Input() appLoadMore: unknown;
  /** Pixels before the end at which loading starts. */
  @Input() loadMoreMargin = 320;
  @Output() loadMore = new EventEmitter<void>();

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private observer?: IntersectionObserver;

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => this.observe());
    this.destroyRef.onDestroy(() => this.observer?.disconnect());
  }

  /** A new observer each time, so the root follows whichever ancestor is scrolling at that moment. */
  private observe(): void {
    if (!this.el.nativeElement.isConnected) return;
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.zone.run(() => this.loadMore.emit());
      },
      { root: scrollParent(this.el.nativeElement), rootMargin: `0px 0px ${this.loadMoreMargin}px 0px` }
    );
    this.observer.observe(this.el.nativeElement);
  }

  ngOnChanges(): void {
    // A fresh observation reports the current state again, even if the element never left the screen.
    // (after the new rows are in the page, so the root is picked from the final layout)
    if (this.observer) this.zone.runOutsideAngular(() => requestAnimationFrame(() => this.observe()));
  }
}
